import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Download, FileDown, Loader2, Mail, Save, CalendarClock } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { FIRMA, ORG_LABEL, STATO_BADGE, STATO_COLORS, fmtDate, fmtDateTime } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";
import { Consuntivo } from "@/components/Consuntivo";

export default function SprintReview() {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [plan, setPlan] = useState(null);
  const [sprint, setSprint] = useState("");
  const [stats, setStats] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [invii, setInvii] = useState(null);

  const caricaInvii = () =>
    api.get(`/projects/${projectId}/invii`).then(({ data }) => setInvii(data)).catch(() => setInvii(null));

  useEffect(() => {
    if (!projectId) return;
    setPlan(null);
    setStats(null);
    api.get(`/projects/${projectId}/plan`).then(({ data }) => {
      setPlan(data);
      const active = data.sprints.find((s) => s.percentuale > 0 && s.percentuale < 100) || data.sprints[0];
      setSprint(active?.key || "");
    });
  }, [projectId]);

  useEffect(() => {
    if (!sprint || !projectId) return;
    api.get(`/projects/${projectId}/dashboard`, { params: { sprint } }).then(({ data }) => setStats(data.sprint_corrente));
    api.get(`/projects/${projectId}/reviews/${encodeURIComponent(sprint)}`).then(({ data }) => setNote(data.note || ""));
    caricaInvii();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprint, projectId]);

  const saveNote = async () => {
    setBusy("note");
    try {
      await api.put(`/projects/${projectId}/reviews`, { sprint, note });
      toast.success("Note di review salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy("");
  };

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      await downloadFile(`/projects/${projectId}/export/pdf?sprint=${encodeURIComponent(sprint)}`,
        `SprintReview_${sprint.replace(/\s/g, "")}.pdf`);
      toast.success("PDF Sprint Review generato");
    } catch (e) {
      toast.error("Errore nella generazione del PDF");
    }
    setBusy("");
  };

  const exportExcel = async () => {
    setBusy("xlsx");
    try {
      await downloadFile(`/projects/${projectId}/export/excel`, "Piano_aggiornato.xlsx");
      toast.success("Excel aggiornato scaricato");
    } catch (e) {
      toast.error("Errore nel download Excel");
    }
    setBusy("");
  };

  const inviaEmail = async () => {
    setBusy("email");
    try {
      const { data } = await api.post(`/projects/${projectId}/sprint-email`, { sprint });
      toast.success(
        data.allegato
          ? `Review ${sprint} inviata a ${data.destinatario} con il PDF allegato`
          : `Review ${sprint} inviata a ${data.destinatario} (riepilogo nel corpo email)`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy("");
    caricaInvii();
  };

  if (!plan || !stats)
    return (
      <div className="h-64 grid place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  const sprintDoc = plan.sprints.find((s) => s.key === sprint);
  const rows = plan.tasks.filter((t) => t.sprint === sprint);
  const pieData = stats.distribuzione.filter((d) => d.value > 0);
  const canSend = user.role === "admin" || user.role === "dev";
  const readOnly = ["cliente_finale", "pm", "tester", "fornitore"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="sprint-review-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{plan.project.nome}</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Sprint Review & Export</h1>
          <p className="mt-2 text-sm text-slate-600">
            Anteprima di fine sprint, Excel aggiornato per la tua area e PDF di review firmato.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={sprint} onValueChange={setSprint}>
            <SelectTrigger className="w-[200px] h-10 bg-white" data-testid="review-sprint-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {plan.sprints.map((s) => (<SelectItem key={s.key} value={s.key}>{s.nome}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button onClick={exportExcel} disabled={busy === "xlsx"} variant="outline"
                  className="h-10 bg-white border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
                  data-testid="review-export-excel-button">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Excel
          </Button>
          <Button onClick={exportPdf} disabled={busy === "pdf"}
                  className="h-10 bg-slate-900 text-white hover:bg-slate-800 hover:text-white transition-colors"
                  data-testid="review-export-pdf-button">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
            Genera PDF
          </Button>
          {canSend && (
            <Button onClick={inviaEmail} disabled={busy === "email"}
                    className="h-10 bg-amber-500 text-slate-900 hover:bg-amber-400 transition-colors"
                    data-testid="send-sprint-email-button">
              {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Invia review via email
            </Button>
          )}
        </div>
      </div>

      {sprintDoc && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="review-objective-card">
          <div className="flex flex-wrap justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{sprintDoc.nome}</h2>
              <p className="mt-1 font-mono text-xs text-slate-500">
                {fmtDate(sprintDoc.data_inizio)} → {fmtDate(sprintDoc.data_fine)}
              </p>
            </div>
            <div className="flex gap-8 text-right">
              {user.role !== "cliente" && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 font-mono">% Stefano Critelli</p>
                  <p className="text-4xl font-bold text-indigo-700" data-testid="review-fornitore-progress">
                    {stats.percentuale_fornitore}%
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Consolidato</p>
                <p className="text-4xl font-bold text-slate-900" data-testid="review-sprint-progress">
                  {stats.sprint_percentuale}%
                </p>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">{sprintDoc.obiettivo}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Andamento stati</h3>
          <div className="h-[240px] mt-3">
            {pieData.length === 0 ? (
              <p className="text-sm text-slate-400 pt-14 text-center">Nessun task nello sprint</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={92}
                       paddingAngle={2} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {pieData.map((d) => (<Cell key={d.name} fill={STATO_COLORS[d.name]} stroke="#fff" strokeWidth={2} />))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {[["Task totali", stats.totali], ["Completati", stats.completati],
              ["Task KO", stats.ko], ["Rework tester", stats.rework_totale]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">{l}</p>
                <p className="text-xl font-bold text-slate-900 font-mono">{v}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 pb-3">
            <h3 className="text-lg font-semibold text-slate-900">Tabella avanzamenti</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="review-tasks-table">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr className="text-left">
                  {["Codice", "Task", "Esecutore", "Stato", "% BCN", "% MJB", "RW"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 font-mono whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-700 whitespace-nowrap">{t.codice}</td>
                    <td className="px-3 py-2.5 text-slate-800 min-w-[200px]">{t.task}</td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap text-xs">{ORG_LABEL[t.org]}</td>
                    <td className="px-3 py-2.5">
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${STATO_BADGE[t.stato]}`}>
                        {t.stato}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-indigo-700">{t.percentuale_fornitore}%</td>
                    <td className="px-3 py-2.5 font-mono text-slate-900">{t.percentuale_cliente}%</td>
                    <td className="px-3 py-2.5 font-mono text-rose-600">{t.rework_count || 0}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nessun task</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="review-notes-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Note di review</h3>
            <p className="text-sm text-slate-500">Vengono riportate nel PDF di fine sprint.</p>
          </div>
          {!readOnly && (
            <Button onClick={saveNote} disabled={busy === "note"} variant="outline"
                    className="h-10 bg-white border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
                    data-testid="save-review-note-button">
              {busy === "note" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Salva note
            </Button>
          )}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={6} disabled={readOnly}
                  placeholder="Sintesi dello sprint, criticità aperte, decisioni prese, azioni per il prossimo sprint..."
                  className="mt-4 text-sm" data-testid="review-note-input" />
        <p className="mt-4 pt-4 border-t border-slate-200 font-mono text-xs font-semibold text-slate-900">{FIRMA}</p>
      </div>

      <Consuntivo projectId={projectId} sprint={sprint} />


      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="auto-email-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <CalendarClock className="h-4 w-4 text-amber-600" /> Invio automatico di fine sprint
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Alla data di chiusura di ogni sprint la review viene inviata da sola, con il PDF in allegato, a{" "}
              <span className="font-medium text-slate-700">{invii?.destinatario || "r.leboffe@majorbit.com"}</span>.
              Ogni sprint viene inviato una sola volta.
            </p>
            {sprintDoc?.data_fine && (
              <p className="mt-2 font-mono text-xs text-slate-500" data-testid="auto-email-next">
                {sprint} si chiude il {fmtDate(sprintDoc.data_fine)}: invio previsto quel giorno alle 17:00.
              </p>
            )}
          </div>
        </div>

        {invii?.invii?.length ? (
          <div className="mt-4 space-y-2" data-testid="invii-list">
            {invii.invii.slice(0, 6).map((i) => (
              <div key={i.id}
                   className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs"
                   data-testid={`invio-${i.id}`}>
                <span className="font-medium text-slate-800">{i.sprint}</span>
                <span className="text-slate-500">{fmtDateTime(i.ts)}</span>
                <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] ${i.automatico ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>
                  {i.automatico ? "automatico" : `manuale · ${i.inviato_da}`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-400" data-testid="invii-empty">
            Nessuna review ancora inviata per questo progetto.
          </p>
        )}
      </div>


      <PmBot pagina="review" sprint={sprint || null} />

    </div>
  );
}
